import {
  analisarTelefone,
  formatarTelefone,
} from "../../../shared/utils/telefoneBR";
import { ehDoadorFigado } from "../../../shared/utils/modalidadeAtendimento";
import React, { useState, useEffect } from "react";
import {
  ClipboardList,
  Stethoscope,
  Bot,
  FileText,
  FlaskConical,
  BarChart2,
  Hospital,
  Activity,
  Microscope,
  Pill,
  Image,
  ShieldCheck,
  Cake,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Pencil,
  AlertTriangle,
  Plus,
  User,
  Trash2,
  Eye,
  Package,
  X,
  Phone,
  Save,
  Venus,
  Mars,
} from "lucide-react";
import { usePacientes } from "../context/PacientesContext";
import { useAuth } from "../../auth/context/AuthContext";
import {
  ehMedico,
  podeEscreverClinico,
  ehMultiprofissional,
} from "../../../shared/utils/permissoesCategoria";
import { theme } from "../../../shared/styles/theme";
import ExamesLaboratoriais from "../../exames/components/ExamesLaboratoriais";
import ExamesPreTransplante from "../../exames/components/ExamesPreTransplante";
import DadosTransplante from "../../transplante/components/DadosTransplante";
import TimelineEvolucoes from "../../evolucao/components/TimelineEvolucoes";
import TabelaMarcadores from "../../marcadores/components/TabelaMarcadores";
import TabelaSorologias from "../../sorologias/components/TabelaSorologias";
import ListaExamesImagem from "../../exames-imagem/components/ListaExamesImagem";
import { Paciente } from "../../../types";
import { AbaAnaliseIA } from "../../ia-analise/components/AbaAnaliseIA";
import ProntuarioAlertasIA from "./ProntuarioAlertasIA";
import { QuadroCondutasSugeridas } from "../../ia-analise/components/QuadroCondutasSugeridas";
import AbaAvaliacaoClinica from "./AbaAvaliacaoClinica";
import AbaAvaliacaoDoador from "./AbaAvaliacaoDoador";
import AbaPrescricoes from "../../prescricoes/components/AbaPrescricoes";
import AbaAuditoria from "./AbaAuditoria";
import { gerarPDFTCLE } from "../../legal/services/gerarTCLE";

// ============================================
// 🗓️ FUNÇÕES DE DATA
// ============================================

// Converter de ISO (AAAA-MM-DD) para BR (DD/MM/AAAA)
const formatarDataParaBR = (data: string | undefined): string => {
  if (!data) return "";
  if (data.includes("/")) return data; // Já está no formato BR
  if (data.includes("-")) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  return data;
};

// Converter de BR (DD/MM/AAAA) para ISO (AAAA-MM-DD)
const formatarDataParaISO = (data: string | undefined): string => {
  if (!data) return "";
  if (data.includes("-") && data.length === 10) return data; // Já está no formato ISO
  if (data.includes("/")) {
    const partes = data.split("/");
    if (partes.length === 3) {
      const [dia, mes, ano] = partes;
      // Limita o ano a 4 dígitos
      const anoLimitado = ano.slice(0, 4);
      return `${anoLimitado}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
    }
  }
  return data;
};

// Formatar data enquanto digita (DD/MM/AAAA)
const formatarDataDigitando = (valor: string): string => {
  // Remove tudo que não é número
  const numeros = valor.replace(/\D/g, "");

  // Limita a 8 dígitos (DDMMAAAA)
  const numerosLimitados = numeros.slice(0, 8);

  // Aplica a máscara
  if (numerosLimitados.length <= 2) {
    return numerosLimitados;
  } else if (numerosLimitados.length <= 4) {
    return `${numerosLimitados.slice(0, 2)}/${numerosLimitados.slice(2)}`;
  } else {
    return `${numerosLimitados.slice(0, 2)}/${numerosLimitados.slice(
      2,
      4
    )}/${numerosLimitados.slice(4)}`;
  }
};

// ═══════════════════════════════════════════════════════
// ACESSORES COM FALLBACK (inscricaoTx legado → campos planos)
// ═══════════════════════════════════════════════════════
function getDataTransplantePac(p: Paciente): string | undefined {
  return p.inscricaoTx?.dataTransplante || p.dataTransplante;
}
function getNumeroRGCTPac(p: Paciente): string | undefined {
  return p.inscricaoTx?.numeroRGCT || p.numeroRGCT || p.rgct;
}
function getHospitalTxPac(p: Paciente): string | undefined {
  return p.inscricaoTx?.hospitalTransplante || p.hospitalTransplante;
}

export default function VisualizacaoPaciente() {
  const { pacienteSelecionado, desselecionarPaciente, atualizarPaciente } =
    usePacientes();
  const { usuarioLogado } = useAuth();
  // 🔐 B88 (AI_98): nome legado - hoje significa "somente leitura".
  // Cobre convidado E observador (espelho de canWriteClinical das rules).
  const isConvidado = !podeEscreverClinico(usuarioLogado);
  const [abaSelecionada, setAbaSelecionada] = useState("dados");
  const [modoEdicao, setModoEdicao] = useState(false);
  const [dadosEdicao, setDadosEdicao] = useState<Paciente | null>(null);
  const [modoEdicaoInscricao, setModoEdicaoInscricao] = useState(false); // ← ADICIONAR
  const [dadosEdicaoInscricao, setDadosEdicaoInscricao] = useState<any>(null); // ← ADICIONAR

  // ✅ ADICIONAR ESTE useEffect AQUI (depois dos useState)
  useEffect(() => {
    // Desabilitar scroll da página principal
    document.body.style.overflow = "hidden";

    // Reabilitar quando componente desmontar (fechar modal)
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  if (!pacienteSelecionado) return null;

  if (!pacienteSelecionado) return null;

  // 📊 CALCULAR TEMPO DE TRANSPLANTE
  const calcularTempoTransplante = (dataTransplante: string) => {
    // Normalizar para ISO antes de processar
    const iso = formatarDataParaISO(dataTransplante);
    if (!iso || !iso.includes("-")) return "data inválida";
    const [ano, mes, dia] = iso.split("-").map(Number);
    if (isNaN(ano) || isNaN(mes) || isNaN(dia)) return "data inválida";
    const dataTx = new Date(ano, mes - 1, dia); // mes - 1 porque JavaScript usa 0-11
    const hoje = new Date();

    // Zerar horas para comparar só as datas
    hoje.setHours(0, 0, 0, 0);
    dataTx.setHours(0, 0, 0, 0);

    const diffMs = hoje.getTime() - dataTx.getTime();
    const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const anos = Math.floor(diffDias / 365);
    const mesesRestantes = Math.floor((diffDias % 365) / 30);
    const diasRestantes = Math.floor((diffDias % 365) % 30);

    if (anos > 0) {
      return `${anos} ano${anos > 1 ? "s" : ""} e ${mesesRestantes} ${
        mesesRestantes === 1 ? "mês" : "meses"
      }`;
    } else if (mesesRestantes > 0) {
      return `${mesesRestantes} ${
        mesesRestantes === 1 ? "mês" : "meses"
      } e ${diasRestantes} ${diasRestantes === 1 ? "dia" : "dias"}`;
    } else {
      return `${diasRestantes} ${diasRestantes === 1 ? "dia" : "dias"}`;
    }
  };

  // 🎨 DEFINIR COR DO CABEÇALHO
  const corCabecalho = getDataTransplantePac(pacienteSelecionado)
    ? "#1e3a8a" // Azul marinho para pós-Tx
    : theme.primary[600]; // Azul padrão para pré-Tx

  // 🎨 ESTILOS PARA ELEMENTOS FIXOS
  const headerPacienteStyle: React.CSSProperties = {
    // ❌ REMOVER position: "sticky"
    // ❌ REMOVER top: "0px"
    zIndex: 999,
    background: corCabecalho,
    padding: "20px 32px",
    color: "white",
    boxShadow: theme.shadows.lg,
  };

  const abasFixasStyle: React.CSSProperties = {
    background: theme.gray[50],
    borderBottom: `2px solid ${theme.gray[200]}`,
    padding: "8px 32px 0 32px", // ✅ Reduzir de 16px para 8px
    // ❌ REMOVER marginTop: "16px"
  };

  // Abas que têm scroll próprio interno — desativar scroll externo nelas
  const conteudoScrollStyle: React.CSSProperties = {
    padding: "32px",
    paddingTop: "24px",
    background: theme.gray[50],
    flex: 1,
    overflowY: "auto",
  };

  // 📋 DEFINIÇÃO DAS ABAS
  const abas: { id: string; label: string; icon: React.ElementType }[] = [
    {
      id: "dados",
      label: "Dados",
      icon: ClipboardList,
    },
    // ✅ CONSULTAS EM SEGUNDO LUGAR
    {
      id: "evolucao",
      label: "Consultas",
      icon: Stethoscope,
    },
    // 🤖 NOVA ABA - ANÁLISE IA
    {
      id: "analise-ia",
      label: "Análise IA",
      icon: Bot,
    },
    // Avaliação Clínica: aba dedicada só p/ fígado PRÉ-Tx. No pós-Tx ela vive como
    // sub-aba dentro de "Pré-Transplante" (ExamesPreTransplante), agora editável.
    ...(pacienteSelecionado.tipoAtendimento === "figado" &&
    !getDataTransplantePac(pacienteSelecionado)
      ? [
          {
            id: "inscricao",
            label: "Avaliação Clínica",
            icon: FileText,
          },
        ]
      : []),
    // B351 (AI_152) — a aba do DOADOR é exclusiva da modalidade dele: substitui a
    // Avaliação Clínica do receptor, que não se aplica a quem doa.
    ...(ehDoadorFigado(pacienteSelecionado.tipoAtendimento)
      ? [
          {
            id: "doador",
            label: "Avaliação do Doador",
            icon: FileText,
          },
        ]
      : []),
    {
      id: "exames",
      label: getDataTransplantePac(pacienteSelecionado)
        ? "Folhão"
        : "Laboratório",
      icon: getDataTransplantePac(pacienteSelecionado)
        ? ClipboardList
        : FlaskConical,
    },
    ...(getDataTransplantePac(pacienteSelecionado)
      ? [
          {
            id: "pretx",
            label: "Pré-Transplante",
            icon: BarChart2,
          },
          {
            id: "dadostx",
            label: "Dados do Tx",
            icon: Hospital,
          },
        ]
      : []),
    {
      id: "marcadores",
      label: "Marcadores",
      icon: Activity,
    },
    {
      id: "sorologias",
      label: "Sorologias",
      icon: Microscope,
    },
    {
      id: "prescricoes",
      label: "Prescrições",
      icon: Pill,
    },
    {
      id: "imagem",
      label: "Imagem",
      icon: Image,
    },
    {
      id: "auditoria",
      label: "Auditoria",
      icon: ShieldCheck,
    },
  ];
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.8)",
        zIndex: 9999,
        overflow: "hidden",
        display: "flex", // ✅ ADICIONAR
        alignItems: "center", // ✅ ADICIONAR
        justifyContent: "center", // ✅ ADICIONAR
      }}
      onClick={desselecionarPaciente}
    >
      <div
        style={{
          height: "100vh", // ✅ Altura total
          width: "100vw", // ✅ MUDAR para largura total
          maxWidth: "none", // ✅ REMOVER limite de largura
          background: theme.gray[50],
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: "0px", // ✅ REMOVER bordas arredondadas
          boxShadow: "none", // ✅ REMOVER sombra
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 🔒 CABEÇALHO FIXO DO PACIENTE */}
        <div style={headerPacienteStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            {/* INFORMAÇÕES DO PACIENTE */}
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: "bold",
                  margin: "0 0 12px 0",
                  fontFamily: theme.fonts.sans, // ⬅️ ADICIONADO
                }}
              >
                {modoEdicao ? (
                  <input
                    type="text"
                    value={(dadosEdicao || pacienteSelecionado).nome || ""}
                    onChange={(e) => {
                      const pacienteAtualizado = {
                        ...(dadosEdicao || pacienteSelecionado),
                        nome: e.target.value,
                      };
                      setDadosEdicao(pacienteAtualizado);
                    }}
                    style={{
                      fontSize: "32px",
                      fontWeight: "bold",
                      fontFamily: theme.fonts.sans,
                      background: "rgba(255, 255, 255, 0.2)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      borderRadius: theme.borderRadius.md,
                      padding: "8px 12px",
                      color: "white",
                      width: "500px", // ← MUDAR AQUI
                      maxWidth: "100%", // ← ADICIONAR (responsivo)
                    }}
                  />
                ) : (
                  pacienteSelecionado.nome
                )}
              </h2>
              <div
                style={{
                  display: "flex",
                  gap: "24px",
                  fontSize: "14px",
                  opacity: 0.95,
                  flexWrap: "wrap",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <ClipboardList size={14} style={{ marginRight: "6px" }} />{" "}
                  CPF:{" "}
                  {modoEdicao ? (
                    <input
                      type="text"
                      value={(dadosEdicao || pacienteSelecionado).cpf || ""}
                      onChange={(e) => {
                        const pacienteAtualizado = {
                          ...(dadosEdicao || pacienteSelecionado),
                          cpf: e.target.value,
                        };
                        setDadosEdicao(pacienteAtualizado);
                      }}
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: theme.borderRadius.sm,
                        padding: "4px 8px",
                        color: "white",
                        fontSize: "14px",
                        width: "140px",
                        fontFamily: theme.fonts.sans,
                      }}
                    />
                  ) : (
                    pacienteSelecionado.cpf
                  )}
                </span>

                {/* BADGE LISTA DE ESPERA + RGCT */}
                {pacienteSelecionado.tipoAtendimento === "figado" &&
                  !getDataTransplantePac(pacienteSelecionado) &&
                  (pacienteSelecionado.inscricaoTx?.statusInscricao ===
                    "lista_espera" ||
                    getNumeroRGCTPac(pacienteSelecionado)) && (
                    <>
                      {/* Badge Lista de Espera */}
                      <span
                        style={{
                          background: "rgba(251, 191, 36, 0.2)",
                          border: "2px solid rgba(251, 191, 36, 0.4)",
                          padding: "6px 16px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "600",
                          fontFamily: theme.fonts.sans,
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Hospital size={14} style={{ marginRight: "6px" }} />{" "}
                        Lista de Espera
                      </span>
                      {/* RGCT */}
                      {getNumeroRGCTPac(pacienteSelecionado) && (
                        <span
                          style={{
                            background: "rgba(255, 255, 255, 0.2)",
                            padding: "6px 16px",
                            borderRadius: "20px",
                            fontSize: "13px",
                            fontWeight: "600",
                            border: "1px solid rgba(255, 255, 255, 0.3)",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <ClipboardList
                            size={14}
                            style={{ marginRight: "6px" }}
                          />{" "}
                          RGCT: {getNumeroRGCTPac(pacienteSelecionado)}
                        </span>
                      )}
                    </>
                  )}

                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Cake size={14} style={{ marginRight: "6px" }} />{" "}
                  {modoEdicao ? (
                    <input
                      type="text"
                      placeholder="DD/MM/AAAA"
                      value={formatarDataParaBR(
                        (dadosEdicao || pacienteSelecionado).dataNascimento
                      )}
                      onChange={(e) => {
                        const valorFormatado = formatarDataDigitando(
                          e.target.value
                        );
                        const pacienteAtualizado = {
                          ...(dadosEdicao || pacienteSelecionado),
                          dataNascimento: valorFormatado,
                        };
                        setDadosEdicao(pacienteAtualizado);
                      }}
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: theme.borderRadius.sm,
                        padding: "4px 8px",
                        color: "white",
                        fontSize: "14px",
                        width: "110px",
                        fontFamily: theme.fonts.sans,
                      }}
                    />
                  ) : (
                    (() => {
                      const data = pacienteSelecionado.dataNascimento;
                      if (data && data.includes("/")) {
                        return data;
                      }
                      if (data && data.includes("-")) {
                        const [ano, mes, dia] = data.split("-");
                        return `${dia}/${mes}/${ano}`;
                      }
                      return data || "Não informado";
                    })()
                  )}
                </span>

                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {(dadosEdicao || pacienteSelecionado).sexo === "M" ? (
                    <Mars size={16} />
                  ) : (
                    <Venus size={16} />
                  )}{" "}
                  {modoEdicao ? (
                    <select
                      value={(dadosEdicao || pacienteSelecionado).sexo || ""}
                      onChange={(e) => {
                        const pacienteAtualizado = {
                          ...(dadosEdicao || pacienteSelecionado),
                          sexo: e.target.value as "M" | "F",
                        };
                        setDadosEdicao(pacienteAtualizado);
                      }}
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: theme.borderRadius.sm,
                        padding: "4px 8px",
                        color: "white",
                        fontSize: "14px",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <option value="M" style={{ color: "black" }}>
                        Masculino
                      </option>
                      <option value="F" style={{ color: "black" }}>
                        Feminino
                      </option>
                    </select>
                  ) : (dadosEdicao || pacienteSelecionado).sexo === "M" ? (
                    "Masculino"
                  ) : (
                    "Feminino"
                  )}
                </span>

                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Phone size={14} style={{ marginRight: "6px" }} />{" "}
                  {modoEdicao ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      <input
                        type="text"
                        value={
                          (dadosEdicao || pacienteSelecionado).telefone || ""
                        }
                        onChange={(e) => {
                          const pacienteAtualizado = {
                            ...(dadosEdicao || pacienteSelecionado),
                            telefone: e.target.value,
                          };
                          setDadosEdicao(pacienteAtualizado);
                        }}
                        // B345 (AI_150) — formata ao SAIR do campo.
                        onBlur={(e) =>
                          setDadosEdicao({
                            ...(dadosEdicao || pacienteSelecionado),
                            telefone: formatarTelefone(e.target.value),
                          })
                        }
                        style={{
                          background: "rgba(255, 255, 255, 0.2)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          borderRadius: theme.borderRadius.sm,
                          padding: "4px 8px",
                          color: "white",
                          fontSize: "14px",
                          width: "130px",
                          fontFamily: theme.fonts.sans,
                        }}
                      />
                      {/* B345 — é ESTE o telefone que recebe a receita por
                          WhatsApp, e este cabeçalho é a ÚNICA tela onde o número
                          de um paciente já cadastrado pode ser editado. */}
                      {(() => {
                        const an = analisarTelefone(
                          (dadosEdicao || pacienteSelecionado).telefone
                        );
                        if (!an.aviso) return null;
                        return (
                          <span
                            title={an.aviso}
                            style={{
                              background: "#fef3c7",
                              color: "#92400e",
                              fontSize: "11px",
                              fontWeight: 700,
                              padding: "2px 8px",
                              borderRadius: "999px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            ⚠️ {an.tipo === "fixo" ? "Fixo — não recebe WhatsApp" : an.aviso}
                          </span>
                        );
                      })()}
                    </span>
                  ) : (
                    pacienteSelecionado.telefone
                  )}
                </span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Hospital size={14} style={{ marginRight: "6px" }} />{" "}
                  {modoEdicao ? (
                    <input
                      type="text"
                      value={(dadosEdicao || pacienteSelecionado).hospub || ""}
                      onChange={(e) => {
                        const pacienteAtualizado = {
                          ...(dadosEdicao || pacienteSelecionado),
                          hospub: e.target.value,
                        };
                        setDadosEdicao(pacienteAtualizado);
                      }}
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: theme.borderRadius.sm,
                        padding: "4px 8px",
                        color: "white",
                        fontSize: "14px",
                        width: "130px",
                        fontFamily: theme.fonts.sans,
                      }}
                    />
                  ) : (
                    pacienteSelecionado.hospub || "—"
                  )}
                </span>
              </div>

              {/* BADGES DE STATUS */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginTop: "16px",
                  flexWrap: "wrap",
                }}
              >
                {/* TIPO DE TRANSPLANTE */}
                {pacienteSelecionado.tipoAtendimento !== "ambulatorial" && (
                  <span
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontSize: "13px",
                      fontWeight: "600",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Hospital size={14} style={{ marginRight: "6px" }} />{" "}
                    {/* B351 (AI_152): sem o ramo do doador, ele caía no ÚLTIMO
                        ramo e aparecia como "Transplante Rim-Pâncreas". */}
                    {ehDoadorFigado(pacienteSelecionado.tipoAtendimento)
                      ? "Doador de Fígado (intervivos)"
                      : pacienteSelecionado.tipoAtendimento === "figado"
                      ? "Transplante de Fígado"
                      : pacienteSelecionado.tipoAtendimento === "rim"
                      ? "Transplante de Rim"
                      : pacienteSelecionado.tipoAtendimento === "pancreas"
                      ? "Transplante de Pâncreas"
                      : "Transplante Rim-Pâncreas"}
                  </span>
                )}

                {/* SE FOR PÓS-TRANSPLANTE */}
                {getDataTransplantePac(pacienteSelecionado) ? (
                  <>
                    {/* DATA DO TRANSPLANTE */}
                    <span
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        padding: "6px 16px",
                        borderRadius: "20px",
                        fontSize: "13px",
                        fontWeight: "600",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <Calendar size={14} style={{ marginRight: "6px" }} /> Tx:{" "}
                      {formatarDataParaBR(
                        (() => {
                          const txs = pacienteSelecionado.transplantes;
                          if (txs?.length) {
                            // Buscar o TX mais recente com data preenchida
                            for (let i = txs.length - 1; i >= 0; i--) {
                              if (txs[i].dataTransplante)
                                return txs[i].dataTransplante;
                            }
                          }
                          return (
                            getDataTransplantePac(pacienteSelecionado) || ""
                          );
                        })()
                      )}
                    </span>

                    {/* TEMPO DE TRANSPLANTE */}
                    <span
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        padding: "6px 16px",
                        borderRadius: "20px",
                        fontSize: "13px",
                        fontWeight: "600",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <Clock size={14} style={{ marginRight: "6px" }} /> Há{" "}
                      {calcularTempoTransplante(
                        pacienteSelecionado.transplantes?.length
                          ? pacienteSelecionado.transplantes[
                              pacienteSelecionado.transplantes.length - 1
                            ].dataTransplante ||
                              getDataTransplantePac(pacienteSelecionado)!
                          : getDataTransplantePac(pacienteSelecionado)!
                      )}
                    </span>

                    {/* CENTRO TRANSPLANTADOR */}
                    {getHospitalTxPac(pacienteSelecionado) && (
                      <span
                        style={{
                          background: "rgba(255, 255, 255, 0.2)",
                          padding: "6px 16px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "600",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Hospital size={14} style={{ marginRight: "6px" }} />{" "}
                        {getHospitalTxPac(pacienteSelecionado)}
                      </span>
                    )}
                  </>
                ) : (
                  /* SE FOR PRÉ-TRANSPLANTE */
                  <>
                    {pacienteSelecionado.statusTransplante && (
                      <span
                        style={{
                          background: "rgba(255, 255, 255, 0.2)",
                          padding: "6px 16px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "600",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {pacienteSelecionado.statusTransplante === "pre" ? (
                          <>
                            <Clock size={12} style={{ marginRight: "4px" }} />{" "}
                            Pré-Transplante
                          </>
                        ) : pacienteSelecionado.statusTransplante ===
                          "lista" ? (
                          <>
                            <ClipboardList
                              size={12}
                              style={{ marginRight: "4px" }}
                            />{" "}
                            Lista de Espera
                          </>
                        ) : (
                          <>
                            <CheckCircle
                              size={12}
                              style={{ marginRight: "4px" }}
                            />{" "}
                            Pós-Transplante
                          </>
                        )}
                      </span>
                    )}
                  </>
                )}

                {/* AMBULATORIAL */}
                {pacienteSelecionado.tipoAtendimento === "ambulatorial" && (
                  <span
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontSize: "13px",
                      fontWeight: "600",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Hospital size={14} style={{ marginRight: "6px" }} />{" "}
                    Ambulatorial
                  </span>
                )}
              </div>
              {/* STATUS DO PACIENTE NO SERVIÇO */}
              {pacienteSelecionado.statusPaciente &&
                pacienteSelecionado.statusPaciente !== "ativo" && (
                  <span
                    style={{
                      background:
                        pacienteSelecionado.statusPaciente === "obito"
                          ? "rgba(0, 0, 0, 0.4)"
                          : pacienteSelecionado.statusPaciente ===
                            "perda_seguimento"
                          ? "rgba(239, 68, 68, 0.3)"
                          : "rgba(249, 115, 22, 0.3)",
                      border:
                        pacienteSelecionado.statusPaciente === "obito"
                          ? "2px solid rgba(0, 0, 0, 0.6)"
                          : "2px solid rgba(239, 68, 68, 0.5)",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      marginTop: "8px",
                      fontSize: "13px",
                      fontWeight: "700",
                      fontFamily: theme.fonts.sans,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {pacienteSelecionado.statusPaciente === "obito" && (
                      <>
                        <XCircle size={12} style={{ marginRight: "4px" }} />{" "}
                        Óbito
                      </>
                    )}
                    {pacienteSelecionado.statusPaciente ===
                      "perda_seguimento" && (
                      <>
                        <XCircle size={12} style={{ marginRight: "4px" }} />{" "}
                        Perda de Seguimento
                      </>
                    )}
                    {pacienteSelecionado.statusPaciente ===
                      "retirado_lista" && (
                      <>
                        <XCircle size={12} style={{ marginRight: "4px" }} />{" "}
                        Retirado da Lista
                      </>
                    )}
                    {pacienteSelecionado.statusPaciente === "transferido" && (
                      <>
                        <Clock size={12} style={{ marginRight: "4px" }} />{" "}
                        Transferido
                      </>
                    )}
                    {pacienteSelecionado.statusPaciente === "alta" && (
                      <>
                        <CheckCircle size={12} style={{ marginRight: "4px" }} />{" "}
                        Alta
                      </>
                    )}
                  </span>
                )}
              {/* DROPDOWN STATUS - MODO EDIÇÃO */}
              {modoEdicao && (
                <select
                  value={
                    (dadosEdicao || pacienteSelecionado).statusPaciente ||
                    "ativo"
                  }
                  onChange={(e) => {
                    const pacienteAtualizado = {
                      ...(dadosEdicao || pacienteSelecionado),
                      statusPaciente: e.target.value as
                        | "ativo"
                        | "obito"
                        | "perda_seguimento"
                        | "retirado_lista"
                        | "transferido"
                        | "alta",
                      dataStatusPaciente:
                        e.target.value !== "ativo"
                          ? new Date().toISOString()
                          : undefined,
                    };
                    setDadosEdicao(pacienteAtualizado);
                  }}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "2px solid rgba(255, 255, 255, 0.4)",
                    borderRadius: "20px",
                    padding: "6px 16px",
                    marginTop: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "white",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <option value="ativo" style={{ color: "#000" }}>
                    ✅ Ativo
                  </option>
                  <option value="obito" style={{ color: "#000" }}>
                    🕊️ Óbito
                  </option>
                  <option value="perda_seguimento" style={{ color: "#000" }}>
                    ❌ Perda de Seguimento
                  </option>
                  <option value="retirado_lista" style={{ color: "#000" }}>
                    ⏸️ Retirado da Lista
                  </option>
                  <option value="transferido" style={{ color: "#000" }}>
                    🔄 Transferido
                  </option>
                  <option value="alta" style={{ color: "#000" }}>
                    ✅ Alta
                  </option>
                </select>
              )}
            </div>
            {/* BOTÕES DE AÇÃO */}
            <div style={{ display: "flex", gap: "12px" }}>
              {/* 🖨️ TCLE (B113/B211): imprime o termo de consentimento de uso de
                  IA com o nome do paciente preenchido, p/ assinatura física. */}
              {!isConvidado && (
                <button
                  onClick={() => gerarPDFTCLE(pacienteSelecionado)}
                  title="Imprimir o Termo de Consentimento (uso de IA) deste paciente para assinatura"
                  style={{
                    padding: "12px 24px",
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "white",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: theme.borderRadius.lg,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                    transition: "all 0.3s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                  }}
                >
                  🖨️ Imprimir TCLE
                </button>
              )}
              <button
                onClick={desselecionarPaciente}
                style={{
                  padding: "12px 24px",
                  background: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRadius: theme.borderRadius.lg,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                  transition: "all 0.3s",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                ✕ Fechar
              </button>
            </div>
          </div>
        </div>
        {/* 🔒 ABAS FIXAS */}
        <div style={abasFixasStyle}>
          <div
            style={{
              display: "flex",
              gap: "4px",
              overflowX: "auto",
              overflowY: "hidden",
              whiteSpace: "nowrap",
              paddingBottom: "4px",
            }}
          >
            {abas.map((aba) => {
              const isActive = abaSelecionada === aba.id;
              return (
                <button
                  key={aba.id}
                  onClick={() => setAbaSelecionada(aba.id)}
                  // 🎨 AI_116 — estilo "sublinhado" flat (mesmo das sub-abas da
                  // Pesquisa Clínica): sem gradiente/formato-pasta/elevação; ativa =
                  // texto azul + linha inferior azul; inativa = cinza (hover escurece).
                  style={{
                    padding: "8px 14px",
                    flexShrink: 0,
                    background: "none",
                    color: isActive ? theme.primary[700] : theme.gray[500],
                    border: "none",
                    borderBottom: isActive
                      ? `2px solid ${theme.primary[600]}`
                      : "2px solid transparent",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                    transition: "color 0.2s ease, border-color 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  onMouseOver={(e) => {
                    if (!isActive) e.currentTarget.style.color = theme.gray[700];
                  }}
                  onMouseOut={(e) => {
                    if (!isActive) e.currentTarget.style.color = theme.gray[500];
                  }}
                >
                  <aba.icon size={16} />
                  <span>{aba.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 📜 CONTEÚDO COM SCROLL */}
        <div style={conteudoScrollStyle}>
          {/* 🤖 ALERTAS DE IA DO PACIENTE */}
          <ProntuarioAlertasIA paciente={pacienteSelecionado} />
          {/* ☑️ B390 (AI_157) — condutas sugeridas pela última análise geral,
              só na aba Consultas, abaixo dos alertas. */}
          {abaSelecionada === "evolucao" && (
            <QuadroCondutasSugeridas
              paciente={pacienteSelecionado}
              onVerAnalise={() => setAbaSelecionada("analise-ia")}
            />
          )}
          {/* ABA: DADOS DO PACIENTE */}
          {abaSelecionada === "dados" && (
            <div
              style={{
                background: "white",
                borderRadius: theme.borderRadius.xl,
                boxShadow: theme.shadows.md,
                padding: "16px",
                maxHeight: "calc(100vh - 300px)",
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: theme.gray[800],
                    fontFamily: theme.fonts.sans,
                    margin: 0,
                  }}
                >
                  <ClipboardList size={16} style={{ marginRight: "8px" }} />{" "}
                  Informações do Paciente
                </h3>
                <div style={{ display: "flex", gap: "12px" }}>
                  {modoEdicao ? (
                    <>
                      <button
                        onClick={() => {
                          if (dadosEdicao) {
                            // Converte a data para formato ISO antes de salvar
                            const dadosParaSalvar = {
                              ...dadosEdicao,
                              dataNascimento: formatarDataParaISO(
                                dadosEdicao.dataNascimento
                              ),
                            };
                            atualizarPaciente(
                              pacienteSelecionado.id,
                              dadosParaSalvar
                            );
                          }
                          setModoEdicao(false);
                          setDadosEdicao(null);
                        }}
                        style={{
                          padding: "10px 20px",
                          background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                          color: "white",
                          border: "none",
                          borderRadius: theme.borderRadius.lg,
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Save size={14} style={{ marginRight: "6px" }} /> Salvar
                        Alterações
                      </button>
                      <button
                        onClick={() => {
                          setModoEdicao(false);
                          setDadosEdicao(null);
                        }}
                        style={{
                          padding: "10px 20px",
                          background: theme.gray[300],
                          color: theme.gray[700],
                          border: "none",
                          borderRadius: theme.borderRadius.lg,
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <X size={14} style={{ marginRight: "6px" }} /> Cancelar
                      </button>
                    </>
                  ) : (
                    !isConvidado && (
                      <button
                        onClick={() => {
                          setDadosEdicao({ ...pacienteSelecionado });
                          setModoEdicao(true);
                        }}
                        style={{
                          padding: "10px 20px",
                          background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                          color: "white",
                          border: "none",
                          borderRadius: theme.borderRadius.lg,
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Pencil size={14} style={{ marginRight: "6px" }} />{" "}
                        Editar Dados
                      </button>
                    )
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "24px",
                }}
              >
                {/* DADOS BÁSICOS */}
                <div>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      fontFamily: theme.fonts.sans,
                      color: theme.primary[700],
                      marginBottom: "16px",
                    }}
                  >
                    Dados Pessoais
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        RG:
                      </span>
                      {modoEdicao ? (
                        <input
                          type="text"
                          value={(dadosEdicao || pacienteSelecionado).rg || ""}
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              rg: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.rg || "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        CNS (Cartão Nacional de Saúde):
                      </span>
                      {modoEdicao ? (
                        <input
                          type="text"
                          value={(dadosEdicao || pacienteSelecionado).cns || ""}
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              cns: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                          placeholder="000 0000 0000 0000"
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.cns || "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        HOSPUB (SUS):
                      </span>
                      {modoEdicao ? (
                        <input
                          type="text"
                          value={
                            (dadosEdicao || pacienteSelecionado).hospub || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              hospub: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans, // ✅ ADICIONAR ESTA LINHA
                          }}
                        >
                          {pacienteSelecionado.hospub || "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Nome da Mãe:
                      </span>
                      {modoEdicao ? (
                        <input
                          type="text"
                          value={
                            (dadosEdicao || pacienteSelecionado).nomeMae || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              nomeMae: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans, // ✅ ADICIONAR
                          }}
                        >
                          {pacienteSelecionado.nomeMae || "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Estado Civil:
                      </span>
                      {modoEdicao ? (
                        <select
                          value={
                            (dadosEdicao || pacienteSelecionado).estadoCivil ||
                            ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              estadoCivil: e.target.value as
                                | "solteiro"
                                | "casado"
                                | "divorciado"
                                | "viuvo"
                                | "uniao_estavel"
                                | undefined,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="Solteiro(a)">Solteiro(a)</option>
                          <option value="Casado(a)">Casado(a)</option>
                          <option value="Divorciado(a)">Divorciado(a)</option>
                          <option value="Viúvo(a)">Viúvo(a)</option>
                          <option value="União Estável">União Estável</option>
                        </select>
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.estadoCivil || "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Profissão:
                      </span>
                      {modoEdicao ? (
                        <input
                          type="text"
                          value={
                            (dadosEdicao || pacienteSelecionado).profissao || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              profissao: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.profissao || "Não informado"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* TIPO DE ATENDIMENTO */}
                <div>
                  <span
                    style={{
                      color: theme.gray[600],
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    Tipo de Atendimento:
                  </span>
                  {modoEdicao ? (
                    <select
                      value={
                        (dadosEdicao || pacienteSelecionado).tipoAtendimento ||
                        "ambulatorial"
                      }
                      onChange={(e) => {
                        const nova = e.target.value;
                        const atual =
                          (dadosEdicao || pacienteSelecionado)
                            .tipoAtendimento || "ambulatorial";
                        if (nova === atual) return;
                        // Aviso (não bloqueia) só ao SAIR de uma linha com telas
                        // próprias (não-ambulatorial): a troca oculta — sem apagar
                        // — as telas da categoria anterior. Sair de Ambulatório é
                        // livre (reclassificação comum, nada a ocultar).
                        const rotulos: Record<string, string> = {
                          ambulatorial: "Ambulatório",
                          figado: "Fígado",
                          rim: "Rim",
                          pancreas: "Pâncreas",
                          rim_pancreas: "Pâncreas",
                          doador_figado: "Doador de Fígado", // B351-FU
                        };
                        if (atual !== "ambulatorial") {
                          const ok = window.confirm(
                            `Trocar o tipo de atendimento de "${
                              rotulos[atual] || atual
                            }" para "${rotulos[nova] || nova}"?\n\n` +
                              `Isso muda o tipo de acompanhamento e OCULTA as telas ` +
                              `específicas de ${rotulos[atual] || atual}. Os dados NÃO ` +
                              `são apagados e reaparecem se você voltar a categoria.`
                          );
                          if (!ok) return; // mantém a categoria atual
                        }
                        setDadosEdicao({
                          ...(dadosEdicao || pacienteSelecionado),
                          tipoAtendimento: nova,
                        });
                      }}
                      style={{
                        width: "100%",
                        padding: "8px",
                        marginTop: "4px",
                        border: `1px solid ${theme.gray[300]}`,
                        borderRadius: theme.borderRadius.md,
                        fontSize: "14px",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      <option value="ambulatorial">Ambulatorial</option>
                      <option value="figado">Transplante de Fígado</option>
                      <option value="rim">Transplante de Rim</option>
                      <option value="pancreas">Transplante de Pâncreas</option>
                      {/* B351-FU (AI_152): a conversão já existia, faltava a
                          modalidade nova na lista. Necessário porque os doadores
                          já cadastrados entraram como "ambulatorial" ou como
                          "figado" pré-Tx e precisam ser reclassificados. */}
                      <option value="doador_figado">
                        Doador de Fígado (intervivos)
                      </option>
                    </select>
                  ) : (
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontWeight: "600",
                        fontFamily: theme.fonts.sans,
                        color:
                          ehDoadorFigado(pacienteSelecionado.tipoAtendimento)
                            ? "#14b8a6" // turquesa do doador (B351)
                            : pacienteSelecionado.tipoAtendimento === "figado"
                            ? "#16a34a"
                            : pacienteSelecionado.tipoAtendimento === "rim"
                            ? "#be185d"
                            : pacienteSelecionado.tipoAtendimento === "pancreas"
                            ? "#f59e0b"
                            : theme.primary[600],
                      }}
                    >
                      {/* B351 (AI_152): sem o ramo do doador, ele caía no ramo
                          final e era rotulado como "Ambulatorial". */}
                      {ehDoadorFigado(pacienteSelecionado.tipoAtendimento)
                        ? "Doador de Fígado"
                        : pacienteSelecionado.tipoAtendimento === "figado"
                        ? "Transplante de Fígado"
                        : pacienteSelecionado.tipoAtendimento === "rim"
                        ? "Transplante de Rim"
                        : pacienteSelecionado.tipoAtendimento === "pancreas"
                        ? "Transplante de Pâncreas"
                        : "Ambulatorial"}
                    </p>
                  )}
                </div>

                {/* ENDEREÇO */}
                <div>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                      marginBottom: "16px",
                    }}
                  >
                    Endereço
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {modoEdicao ? (
                      <>
                        <input
                          type="text"
                          placeholder="Rua"
                          value={
                            (dadosEdicao || pacienteSelecionado).endereco
                              ?.rua || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              endereco: {
                                ...(dadosEdicao || pacienteSelecionado)
                                  .endereco,
                                rua: e.target.value,
                              },
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            padding: "8px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Número"
                          value={
                            (dadosEdicao || pacienteSelecionado).endereco
                              ?.numero || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              endereco: {
                                ...(dadosEdicao || pacienteSelecionado)
                                  .endereco,
                                numero: e.target.value,
                              },
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            padding: "8px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Complemento"
                          value={
                            (dadosEdicao || pacienteSelecionado).endereco
                              ?.complemento || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              endereco: {
                                ...(dadosEdicao || pacienteSelecionado)
                                  .endereco,
                                complemento: e.target.value,
                              },
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            padding: "8px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Bairro"
                          value={
                            (dadosEdicao || pacienteSelecionado).endereco
                              ?.bairro || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              endereco: {
                                ...(dadosEdicao || pacienteSelecionado)
                                  .endereco,
                                bairro: e.target.value,
                              },
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            padding: "8px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Cidade"
                          value={
                            (dadosEdicao || pacienteSelecionado).endereco
                              ?.cidade || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              endereco: {
                                ...(dadosEdicao || pacienteSelecionado)
                                  .endereco,
                                cidade: e.target.value,
                              },
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            padding: "8px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Estado"
                          value={
                            (dadosEdicao || pacienteSelecionado).endereco
                              ?.estado || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              endereco: {
                                ...(dadosEdicao || pacienteSelecionado)
                                  .endereco,
                                estado: e.target.value,
                              },
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            padding: "8px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="CEP"
                          value={
                            (dadosEdicao || pacienteSelecionado).endereco
                              ?.cep || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              endereco: {
                                ...(dadosEdicao || pacienteSelecionado)
                                  .endereco,
                                cep: e.target.value,
                              },
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            padding: "8px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <p style={{ margin: 0, fontFamily: theme.fonts.sans }}>
                          {pacienteSelecionado.logradouro ||
                            pacienteSelecionado.endereco?.rua ||
                            "-"}{" "}
                          {pacienteSelecionado.numero ||
                            pacienteSelecionado.endereco?.numero}
                          {(pacienteSelecionado.complemento ||
                            pacienteSelecionado.endereco?.complemento) &&
                            `, ${
                              pacienteSelecionado.complemento ||
                              pacienteSelecionado.endereco?.complemento
                            }`}
                        </p>
                        <p style={{ margin: 0, fontFamily: theme.fonts.sans }}>
                          {pacienteSelecionado.bairro ||
                            pacienteSelecionado.endereco?.bairro}
                        </p>
                        <p style={{ margin: 0, fontFamily: theme.fonts.sans }}>
                          {pacienteSelecionado.cidade ||
                            pacienteSelecionado.endereco?.cidade}
                          {(pacienteSelecionado.estado ||
                            pacienteSelecionado.endereco?.estado) &&
                            ` - ${
                              pacienteSelecionado.estado ||
                              pacienteSelecionado.endereco?.estado
                            }`}
                        </p>
                        <p style={{ margin: 0, fontFamily: theme.fonts.sans }}>
                          CEP:{" "}
                          {pacienteSelecionado.cep ||
                            pacienteSelecionado.endereco?.cep ||
                            "-"}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* CONTATOS */}
                <div>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                      marginBottom: "16px",
                    }}
                  >
                    Contatos
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Telefone 2:
                      </span>
                      {modoEdicao ? (
                        <input
                          type="text"
                          value={
                            (dadosEdicao || pacienteSelecionado).telefone2 || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              telefone2: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.telefone2 || "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Telefone 3:
                      </span>
                      {modoEdicao ? (
                        <input
                          type="text"
                          value={
                            (dadosEdicao || pacienteSelecionado).telefone3 || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              telefone3: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.telefone3 || "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Email:
                      </span>
                      {modoEdicao ? (
                        <input
                          type="email"
                          value={
                            (dadosEdicao || pacienteSelecionado).email || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              email: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.email || "Não informado"}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* CONTATO DE EMERGÊNCIA */}
                  <div style={{ marginTop: "24px" }}>
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        fontFamily: theme.fonts.sans,
                        color: theme.primary[700],
                        marginBottom: "16px",
                      }}
                    >
                      <AlertTriangle size={16} style={{ marginRight: "8px" }} />{" "}
                      Contato de Emergência
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {modoEdicao ? (
                        <>
                          <input
                            type="text"
                            placeholder="Nome"
                            value={
                              (dadosEdicao || pacienteSelecionado)
                                .contatoEmergencia?.nome || ""
                            }
                            onChange={(e) => {
                              const pacienteAtualizado = {
                                ...(dadosEdicao || pacienteSelecionado),
                                contatoEmergencia: {
                                  ...(dadosEdicao || pacienteSelecionado)
                                    .contatoEmergencia,
                                  nome: e.target.value,
                                },
                              };
                              setDadosEdicao(pacienteAtualizado);
                            }}
                            style={{
                              padding: "8px",
                              border: `1px solid ${theme.gray[300]}`,
                              borderRadius: theme.borderRadius.md,
                              fontSize: "14px",
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Parentesco"
                            value={
                              (dadosEdicao || pacienteSelecionado)
                                .contatoEmergencia?.parentesco || ""
                            }
                            onChange={(e) => {
                              const pacienteAtualizado = {
                                ...(dadosEdicao || pacienteSelecionado),
                                contatoEmergencia: {
                                  ...(dadosEdicao || pacienteSelecionado)
                                    .contatoEmergencia,
                                  parentesco: e.target.value,
                                },
                              };
                              setDadosEdicao(pacienteAtualizado);
                            }}
                            style={{
                              padding: "8px",
                              border: `1px solid ${theme.gray[300]}`,
                              borderRadius: theme.borderRadius.md,
                              fontSize: "14px",
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Telefone"
                            value={
                              (dadosEdicao || pacienteSelecionado)
                                .contatoEmergencia?.telefone || ""
                            }
                            onChange={(e) => {
                              const pacienteAtualizado = {
                                ...(dadosEdicao || pacienteSelecionado),
                                contatoEmergencia: {
                                  ...(dadosEdicao || pacienteSelecionado)
                                    .contatoEmergencia,
                                  telefone: e.target.value,
                                },
                              };
                              setDadosEdicao(pacienteAtualizado);
                            }}
                            style={{
                              padding: "8px",
                              border: `1px solid ${theme.gray[300]}`,
                              borderRadius: theme.borderRadius.md,
                              fontSize: "14px",
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <p
                            style={{
                              margin: 0,
                              fontFamily: theme.fonts.sans, // ✅ ADICIONAR
                            }}
                          >
                            <strong>
                              {pacienteSelecionado.contatoEmergencia?.nome ||
                                "Não informado"}
                            </strong>
                          </p>
                          <p
                            style={{
                              margin: 0,
                              color: theme.gray[600],
                              fontFamily: theme.fonts.sans, // ✅ ADICIONAR
                            }}
                          >
                            {pacienteSelecionado.contatoEmergencia?.parentesco}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontFamily: theme.fonts.sans, // ✅ ADICIONAR
                            }}
                          >
                            {pacienteSelecionado.contatoEmergencia?.telefone}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* DADOS CLÍNICOS */}
                <div>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                      marginBottom: "16px",
                    }}
                  >
                    Dados Clínicos
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Peso:
                      </span>
                      {modoEdicao ? (
                        <input
                          type="number"
                          value={
                            (dadosEdicao || pacienteSelecionado).peso || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              peso: parseFloat(e.target.value) || 0,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.peso
                            ? `${pacienteSelecionado.peso} kg`
                            : "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Altura:
                      </span>
                      {modoEdicao ? (
                        <input
                          type="number"
                          value={
                            (dadosEdicao || pacienteSelecionado).altura || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              altura: parseFloat(e.target.value) || 0,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.altura
                            ? `${pacienteSelecionado.altura} cm`
                            : "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Tipo Sanguíneo:
                      </span>
                      {modoEdicao ? (
                        <select
                          value={
                            (dadosEdicao || pacienteSelecionado)
                              .grupoSanguineo || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              grupoSanguineo: e.target.value as
                                | "A+"
                                | "A-"
                                | "B+"
                                | "B-"
                                | "AB+"
                                | "AB-"
                                | "O+"
                                | "O-"
                                | undefined,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                          }}
                        >
                          <option value="">Selecione</option>
                          <option value="A+">A+</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B-">B-</option>
                          <option value="AB+">AB+</option>
                          <option value="AB-">AB-</option>
                          <option value="O+">O+</option>
                          <option value="O-">O-</option>
                        </select>
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.grupoSanguineo ||
                            "Não informado"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Alergias:
                      </span>
                      {modoEdicao ? (
                        <textarea
                          value={
                            (dadosEdicao || pacienteSelecionado).alergias || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              alergias: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                            minHeight: "60px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.alergias || "Nenhuma informada"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Medicações em Uso:
                      </span>
                      {modoEdicao ? (
                        <textarea
                          value={
                            (dadosEdicao || pacienteSelecionado)
                              .medicacoesEmUso || ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              medicacoesEmUso: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                            minHeight: "60px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.medicacoesEmUso ||
                            "Nenhuma informada"}
                        </p>
                      )}
                    </div>
                    <div>
                      <span
                        style={{
                          color: theme.gray[600],
                          fontSize: "14px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        Comorbidades:
                      </span>
                      {modoEdicao ? (
                        <textarea
                          value={
                            (dadosEdicao || pacienteSelecionado).comorbidades ||
                            ""
                          }
                          onChange={(e) => {
                            const pacienteAtualizado = {
                              ...(dadosEdicao || pacienteSelecionado),
                              comorbidades: e.target.value,
                            };
                            setDadosEdicao(pacienteAtualizado);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            marginTop: "4px",
                            border: `1px solid ${theme.gray[300]}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: "14px",
                            minHeight: "60px",
                          }}
                        />
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pacienteSelecionado.comorbidades ||
                            "Nenhuma informada"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ABA: INSCRIÇÃO PARA TX — 🔐 B88: avaliação clínica é editável
              só pela equipe clínica (espelho das firestore.rules) */}
          {abaSelecionada === "inscricao" && (
            <AbaAvaliacaoClinica
              pacienteId={pacienteSelecionado.id}
              somenteLeitura={
                !ehMedico(usuarioLogado) && !ehMultiprofissional(usuarioLogado)
              }
            />
          )}
          {/* ABA: AVALIAÇÃO DO DOADOR — B351 (AI_152) */}
          {abaSelecionada === "doador" && (
            <AbaAvaliacaoDoador
              pacienteId={pacienteSelecionado.id}
              somenteLeitura={
                !ehMedico(usuarioLogado) && !ehMultiprofissional(usuarioLogado)
              }
            />
          )}
          {/* ABA: EXAMES LABORATORIAIS */}
          {abaSelecionada === "exames" && (
            <div style={{ height: "calc(100vh - 280px)", overflow: "hidden" }}>
              <ExamesLaboratoriais paciente={pacienteSelecionado} />
            </div>
          )}

          {abaSelecionada === "pretx" && (
            <ExamesPreTransplante paciente={pacienteSelecionado} />
          )}

          {abaSelecionada === "dadostx" && (
            <DadosTransplante paciente={pacienteSelecionado} />
          )}

          {/* ABA: EVOLUÇÃO CLÍNICA */}
          {abaSelecionada === "evolucao" && (
            <div
              style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
            >
              <TimelineEvolucoes
                pacienteId={pacienteSelecionado.id}
                pacienteNome={pacienteSelecionado.nome}
                paciente={pacienteSelecionado} // 👈 ADICIONAR ESTA LINHA
              />
            </div>
          )}

          {/* ABA: MARCADORES E AUTOANTICORPOS */}
          {abaSelecionada === "marcadores" && (
            <TabelaMarcadores paciente={pacienteSelecionado} />
          )}

          {/* ABA: SOROLOGIAS VIRAIS */}
          {abaSelecionada === "sorologias" && (
            <div style={{ height: "calc(100vh - 280px)", overflow: "hidden" }}>
              <TabelaSorologias paciente={pacienteSelecionado} />
            </div>
          )}

          {/* ABA: PRESCRIÇÕES */}
          {abaSelecionada === "prescricoes" && (
            <AbaPrescricoes paciente={pacienteSelecionado} />
          )}

          {abaSelecionada === "imagem" && (
            <ListaExamesImagem paciente={pacienteSelecionado} />
          )}
          {/* ABA: ANÁLISE IA */}
          {abaSelecionada === "analise-ia" && (
            <AbaAnaliseIA paciente={pacienteSelecionado} />
          )}
          {/* ABA: AUDITORIA */}
          {abaSelecionada === "auditoria" && (
            <AbaAuditoria paciente={pacienteSelecionado} />
          )}
        </div>
      </div>
    </div>
  );
}
